const photosTemplate = require('20220101/templates/widgets/looff/photos.hbs');
const indexTemplate = require('20220101/templates/widgets/looff/index.hbs');
import widgetMixin from "./widget-mixin.js";
import { throttle } from 'throttle-debounce';

const PER_PAGE = 20;
const MAX_PHOTOS_TO_SHOW = 400;

const DEFAULTS =  {
  numPhotos: 4,
  numPhotosTablet: 3,
  numPhotosPhone: 1
}

const defaultCustomStyles = {
  arrowColor: "#000",
  arrowSize: "30px",
  hoverEnabled: true,
  displayHeader: true,
  displayIcons: true,
  displayShopItButton: true,
}


class LooffWidget extends widgetMixin(HTMLElement) {
  constructor() {
    super();

    this.DEFAULTS = DEFAULTS;

    let customStyles = {};
    // override using this inside l2h-loof-widget tag
    // <template id="style">
    // {
    //   "arrowColor": "#000",
    //   "arrowSize": "30px",
    //   "hoverEnabled": true,
    //   "displayHeader": true,
    //   "displayIcons": true,
    //   "displayShopItButton": true
    // }
    // </template>

    let customStylesTemplate = this.querySelector('template#style');


    if (customStylesTemplate) {
      customStyles = JSON.parse(customStylesTemplate.innerHTML);
    }

    this.customStyles = { ...defaultCustomStyles, ...customStyles };

    this.photos = [];
    this.page = 1;
    this.per = PER_PAGE;

    this.scrollHandler = throttle(200, this.scrollHandler).bind(this);
    this.navClickHandler = this.navClickHandler.bind(this);
  }

  afterRender() {
    if (this.autoScrollDelay) {
      this.enableAutoScroll();
    }
  }

  enableLeftScroller() {
    this.container.querySelector(".l2h-arrow-left").classList.remove("l2h-disabled");
  }

  disableLeftScroller() {
    this.container.querySelector(".l2h-arrow-left").classList.add("l2h-disabled");
  }

  enableRightScroller() {
    this.rightScrollerDisabled = false;
    this.container.querySelector(".l2h-arrow-right").classList.remove("l2h-disabled");
  }

  disableRightScroller() {
    this.rightScrollerDisabled = true;
    this.container.querySelector(".l2h-arrow-right").classList.add("l2h-disabled");
  }

  bindEvents() {
    this.container.addEventListener("click", this.navClickHandler);
    this.bindScrollableEventsHandler = this.scrollableContainer.addEventListener("scroll", this.scrollHandler);
  }

  unbindEvents() {
    if (this.container) { this.container.removeEventListener("click", this.navClickHandler) }
    if (this.scrollableContainer) { this.scrollableContainer.removeEventListener("scroll", this.scrollHandler) }
  }

  navClickHandler(e) {
    this.disableAutoScroll();
    if (e.target.closest(".l2h-arrow-right")) {
      e.preventDefault();
      this.scroll("right");
    } else if (e.target.closest(".l2h-arrow-left")) {
      e.preventDefault();
      this.scroll("left");
    }
  }

  scrollHandler(e) {
    const photoWidth = this.scrollerWidth();
    const scrollLeft = this.scrollableContainer.scrollLeft;
    const distanceToRightEdge = this.scrollableContainer.scrollWidth - (scrollLeft + this.scrollableContainer.clientWidth);

    if (scrollLeft === 0) {
      this.disableLeftScroller();
    } else {
      this.enableLeftScroller();
    }

    if (this.photos.length === this.totalCount) {
      if (distanceToRightEdge <= 0) {
        this.disableRightScroller();
      } else {
        this.enableRightScroller();
      }
    } else {
      if (distanceToRightEdge <= 0) {
        this.DEBUG("L2H DEBUG: fetching next batch of photos");
        this.fetchPhotos();
      }
    }
  }

  enableAutoScroll() {
    this.autoScrollEnabled = true;
    this.autoScroll();
  }

  disableAutoScroll() {
    this.autoScrollEnabled = false;
  }

  autoScroll() {
    if (!this.autoScrollEnabled) { return; }

    if (this.scrollableContainer.scrollLeft > 0 && this.rightScrollerDisabled) {
      this.scrollTo(0);
    }


    this.autoScrollTimeout = setTimeout(() => {
      if (this.autoScrollEnabled) {
        this.scroll("right");
      }
      this.autoScroll();
    }, this.autoScrollDelay);
  }

  scrollerWidth() {
    if (this.photos.length < 2) { return; }

    const photo1 = this.container.querySelector(".l2h-photowrap:nth-of-type(1)");
    const photo2 = this.container.querySelector(".l2h-photowrap:nth-of-type(2)");

    return photo2.offsetLeft - photo1.offsetLeft;
  }

  numberOfPhotosThatCanFit() {
    if (this.photos.length < 2) { return; }

    const photoWidth = this.container.querySelector(".l2h-photowrap").clientWidth;
    const marginWidth = this.scrollerWidth() - photoWidth;

    const innerWidth = this.scrollableContainer.clientWidth;

    return Math.floor((1.0*(innerWidth + (2.0*marginWidth)))/(photoWidth + marginWidth));
  }

  scrollTo(photoIdx) {
    if (this.photos.length < 2) { return; }

    this.DEBUG(`scrolling to index: ${photoIdx}`);
    if (photoIdx < 0) { return; }

    this.scrollableContainer.scrollTo({top: 0, left: this.scrollerWidth() * photoIdx, behavior: "smooth"});
  }

  scroll(direction) {
    let num = this.numberOfPhotosThatCanFit();

    if (direction === "left") {
      num *= -1;
    }

    let scrollToIdx = this.closestScrollIndex + num;
    if (scrollToIdx < 0) { scrollToIdx = 0; }

    if ((scrollToIdx + num) >= this.photos.length) {
      this.fetchPhotos().then(() => {
        this.scrollTo(scrollToIdx);
      });
    } else {
      this.scrollTo(scrollToIdx);
    }
  }

  processPhotos(data) {
    super.processPhotos(data);
    this.totalCount = Math.min(data.meta.totalCount, MAX_PHOTOS_TO_SHOW);
    this.page = data.meta.nextPage;
  }

  appendPhotos(photos) {
    const renderedPhotos = photosTemplate({photos, customStyles: this.customStyles, preferences: this.preferences});
    this.shadow.querySelector('.l2h-photos').insertAdjacentHTML('beforeend', renderedPhotos);
    this.renderHeader();
  }

  renderIndex() {
    if (this.autoScrollTimeout) {
      clearTimeout(this.autoScrollTimeout);
    }
    this.unbindEvents();
    this.disableAutoScroll();

    const renderedIndex = indexTemplate({
      photos: this.photos,
      photoWidth: 100/Number(this.numPhotos),
      photoWidthT: 100/Number(this.numPhotosTablet),
      photoWidthP: 100/Number(this.numPhotosPhone),
      customStyles: this.customStyles,
      preferences: this.preferences,
      uuid: this.uuid
    });
    this.shadow.innerHTML = renderedIndex;
    this.appendCustomCSS();

    this.bindEvents();
  }

  render() {
    this.renderIndex();
    this.appendPhotos(this.photos);
  }

  get container() {
    return this.shadow.querySelector(".l2h-looff");
  }

  get scrollableContainer() {
    return this.shadow.querySelector(".l2h-photos");
  }

  get closestScrollIndex() {
    const offset = this.scrollableContainer.scrollLeft;
    return Math.ceil((1.0*offset)/this.scrollerWidth());
  }
}

customElements.define("l2h-looff-widget", LooffWidget);
